import { apiClient } from './client';

export interface StoryAuthor {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface StoryComment {
  id: string;
  storyId: string;
  authorId: string;
  content: string;
  createdAt: string;
  likeCount: number;
  isPinned: boolean;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
  };
}

export type ContentRating = 'SAFE' | 'SUGGESTIVE' | 'ADULT' | 'BLOCKED';

export interface StoryItem {
  id: string;
  mediaFileId: string;
  thumbnailUrl: string;
  mimeType: string;
  contentRating: ContentRating;
  caption: string | null;
  textOverlay: string | null;
  createdAt: string;
  expiresAt: string;
  viewed: boolean;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
}

export interface StoryGroup {
  author: StoryAuthor;
  stories: StoryItem[];
  hasUnviewed: boolean;
}

export interface StoryDetail {
  id: string;
  author: StoryAuthor;
  mediaFileId: string;
  thumbnailUrl: string;
  caption: string | null;
  textOverlay: string | null;
  privacy: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
  isLiked: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface StoryViewer {
  viewer: StoryAuthor;
  viewedAt: string;
}

export interface MusicTrackMini {
  id: string;
  title: string;
  artist: string;
  coverUrl: string | null;
  audioUrl?: string;
}

export interface ForYouItem {
  id: string;
  author: StoryAuthor;
  mediaFileId: string;
  mediaUrl: string;
  thumbnailUrl: string;
  mimeType: string;
  contentRating: ContentRating;
  caption: string | null;
  textOverlay: string | null;
  musicTrack: MusicTrackMini | null;
  hashtags: string[];
  likeCount: number;
  commentCount: number;
  viewCount: number;
  shareCount: number;
  replyCount: number;
  isLiked: boolean;
  parentStoryId: string | null;
  replyMode: 'NONE' | 'DUET' | 'STITCH';
  createdAt: string;
  expiresAt: string;
}

export const storiesApi = {
  async create(data: {
    mediaFileId: string;
    caption?: string;
    textOverlay?: string;
    privacy?: 'ALL' | 'CONTACTS' | 'NOBODY';
    musicTrackId?: string;
    parentStoryId?: string;
    replyMode?: 'DUET' | 'STITCH';
  }) {
    const res = await apiClient.post('/api/stories', data);
    return res.data;
  },

  async getForYou(cursor?: string, limit = 20): Promise<{ items: ForYouItem[]; nextCursor: string | null }> {
    const res = await apiClient.get('/api/stories/for-you', { params: { cursor, limit } });
    return res.data;
  },

  async recordShare(id: string): Promise<void> {
    await apiClient.post(`/api/stories/${id}/share`);
  },

  async getReplies(id: string, cursor?: string): Promise<{ items: ForYouItem[]; nextCursor: string | null }> {
    const res = await apiClient.get(`/api/stories/${id}/replies`, { params: { cursor } });
    return res.data;
  },

  // Phase 11.2
  async updateProgress(id: string, progressSec: number, completion: number): Promise<void> {
    await apiClient.post(`/api/stories/${id}/progress`, { progressSec, completion });
  },

  async getAnalytics(id: string) {
    const res = await apiClient.get(`/api/stories/${id}/analytics`);
    return res.data as {
      story: { id: string; createdAt: string; expiresAt: string; mimeType: string };
      totals: { views: number; likes: number; comments: number; shares: number; replies: number };
      avgCompletionPct: number;
      retention: Array<{ bucketStartPct: number; count: number }>;
    };
  },

  async toggleCommentLike(commentId: string) {
    const res = await apiClient.post(`/api/stories/comments/${commentId}/like`);
    return res.data as { liked: boolean; likeCount: number };
  },

  async toggleCommentPin(commentId: string) {
    const res = await apiClient.post(`/api/stories/comments/${commentId}/pin`);
    return res.data as { pinned: boolean };
  },

  async getFeed(): Promise<StoryGroup[]> {
    const res = await apiClient.get<StoryGroup[]>('/api/stories/feed');
    return res.data;
  },

  async getById(id: string): Promise<StoryDetail> {
    const res = await apiClient.get<StoryDetail>(`/api/stories/${id}`);
    return res.data;
  },

  async markViewed(id: string): Promise<void> {
    await apiClient.post(`/api/stories/${id}/view`);
  },

  async getViewers(id: string): Promise<StoryViewer[]> {
    const res = await apiClient.get<StoryViewer[]>(`/api/stories/${id}/viewers`);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/stories/${id}`);
  },

  async toggleLike(id: string): Promise<{ liked: boolean; likeCount: number }> {
    const res = await apiClient.post(`/api/stories/${id}/like`);
    return res.data;
  },

  async getComments(id: string): Promise<StoryComment[]> {
    const res = await apiClient.get<StoryComment[]>(`/api/stories/${id}/comments`);
    return res.data;
  },

  async addComment(id: string, content: string): Promise<StoryComment> {
    const res = await apiClient.post<StoryComment>(`/api/stories/${id}/comments`, { content });
    return res.data;
  },

  async deleteComment(storyId: string, commentId: string): Promise<void> {
    await apiClient.delete(`/api/stories/${storyId}/comments/${commentId}`);
  },
};
